// ============================================================================
// NotificationService - Notification Management & Filtering
// ============================================================================
// Replaces V1's notification logic in ctrl-notifications.js. Manages the bell
// icon badge count, mark-read/mark-all-read, type-based filtering (orders,
// tracking, RMAs, invoices, etc.), and refresh via getNotificationList action.
// ============================================================================

import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { DataService } from './data.service';
import { SessionService } from './session.service';

export interface Notification {
  notifyresponsekey: number;
  sendnotifystatus: number; // 1=Unread, 2=Read, 3=Deleted
  notifytypekey: number;
  responseinfo: string;
  responselink: string;
  tooltip: string;
}

export interface NotifyFilterOption {
  key: number;
  text: string;
  selected: boolean;
}

@Injectable()
export class NotificationService {
  private notificationsSubj = new BehaviorSubject<Notification[]>([]);
  notifications$ = this.notificationsSubj.asObservable();

  private unreadCountSubj = new BehaviorSubject<number>(0);
  unreadCount$ = this.unreadCountSubj.asObservable();

  filterOptions: NotifyFilterOption[] = [
    { key: 7, text: 'New Orders', selected: true },
    { key: 1, text: 'Pricing Responses', selected: true },
    { key: 3, text: 'Product Picked', selected: true },
    { key: 4, text: 'Tracking', selected: true },
    { key: 5, text: 'Delivered', selected: true },
    { key: 6, text: 'Delayed Delivery', selected: true },
    { key: 12, text: 'Invoices', selected: true },
    { key: 11, text: 'Order Cancelled', selected: true },
    { key: 8, text: 'RMA Approved', selected: true },
    { key: 9, text: 'RMA Received', selected: true },
    { key: 10, text: 'RMA Credited', selected: true }
  ];

  constructor(private dataSvc: DataService, private session: SessionService) {}

  init(): void {
    const notifs = this.session.data['mdUserNotify'] || [];
    this.notificationsSubj.next(notifs);
    this.updateUnreadCount();
  }

  refresh(): void {
    this.dataSvc.dgAction('getNotificationList').subscribe({
      next: (result: any) => {
        const notifs = Array.isArray(result) ? result : [];
        this.session.data['mdUserNotify'] = notifs;
        this.notificationsSubj.next(notifs);
        this.updateUnreadCount();
      }
    });
  }

  get filtered(): Notification[] {
    const selectedKeys = new Set(this.filterOptions.filter(o => o.selected).map(o => o.key));
    return this.notificationsSubj.getValue()
      .filter(n => n.sendnotifystatus !== 3 && selectedKeys.has(n.notifytypekey));
  }

  markAsRead(note: Notification): void {
    if (note.sendnotifystatus === 1) {
      note.sendnotifystatus = 2;
      this.saveStatus([note.notifyresponsekey], 2);
      this.updateUnreadCount();
    }
  }

  markAllAsRead(): void {
    const ids: number[] = [];
    for (const n of this.notificationsSubj.getValue()) {
      if (n.sendnotifystatus === 1) {
        n.sendnotifystatus = 2;
        ids.push(n.notifyresponsekey);
      }
    }
    if (ids.length) this.saveStatus(ids, 2);
    this.updateUnreadCount();
  }

  clearAllRead(): void {
    const ids: number[] = [];
    for (const n of this.notificationsSubj.getValue()) {
      if (n.sendnotifystatus === 2) {
        n.sendnotifystatus = 3;
        ids.push(n.notifyresponsekey);
      }
    }
    if (ids.length) this.saveStatus(ids, 3);
  }

  getIconClass(typekey: number): string {
    if ([3, 4, 5, 6, 7].includes(typekey)) return 'pi-truck';
    return 'pi-dollar';
  }

  private saveStatus(ids: number[], status: number): void {
    this.dataSvc.dgGet([{
      action: 'NotificationSetStatus',
      returnas: 'BASE',
      ids: ids.join(','),
      status
    }]).subscribe();
  }

  private updateUnreadCount(): void {
    const count = this.notificationsSubj.getValue()
      .filter(n => n.sendnotifystatus === 1).length;
    this.unreadCountSubj.next(count);
  }
}
